import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RestaurantMembership } from "@/stores/restaurantStore";
import { isAppRole, ROLES, type AppRole } from "@/lib/auth/roles";
import { useRestaurantId } from "@/hooks/useRestaurantId";
import { queryKeys } from "@/rc/keys";
import { usePublicTenantSlug } from "@/hooks/use-public-tenant-slug";

type UrrRow = {
  id: string;
  role: string;
  restaurant_id: string;
  restaurants: { slug: string; name: string; logo_url: string | null } | null;
};

type RestaurantRow = {
  id: string;
  slug: string | null;
  name: string | null;
  logo_url: string | null;
};

function mapMembershipRow(row: UrrRow): RestaurantMembership {
  const raw = typeof row.role === "string" ? row.role.trim() : row.role;
  const role: AppRole = isAppRole(raw) ? raw : "staff";
  return {
    id: row.id,
    restaurantId: row.restaurant_id,
    role,
    slug: row.restaurants?.slug ?? null,
    restaurantName: row.restaurants?.name ?? null,
    logoUrl: row.restaurants?.logo_url ?? null,
  };
}

function mapSuperadminMembershipRow(row: RestaurantRow): RestaurantMembership {
  return {
    id: `sa:${row.id}`,
    restaurantId: row.id,
    role: ROLES.OWNER,
    slug: row.slug,
    restaurantName: row.name,
    logoUrl: row.logo_url ?? null,
  };
}

/** Merge superadmin impersonation list: real `user_roles` wins per restaurant; others are synthetic owner. */
function mergeSuperadminMemberships(
  restaurantRows: RestaurantRow[],
  urrRows: UrrRow[]
): RestaurantMembership[] {
  const realByRestaurant = new Map<string, RestaurantMembership>();
  for (const row of urrRows) {
    realByRestaurant.set(row.restaurant_id, mapMembershipRow(row));
  }

  const merged: RestaurantMembership[] = [];
  const seenRestaurantIds = new Set<string>();

  for (const r of restaurantRows) {
    seenRestaurantIds.add(r.id);
    merged.push(realByRestaurant.get(r.id) ?? mapSuperadminMembershipRow(r));
  }

  for (const [restaurantId, membership] of realByRestaurant) {
    if (!seenRestaurantIds.has(restaurantId)) {
      merged.push(membership);
    }
  }

  merged.sort((a, b) =>
    (a.restaurantName ?? "").localeCompare(b.restaurantName ?? "")
  );
  return merged;
}

/** Active `user_roles` rows for the signed-in user (RestaurantGuard, switcher). */
export function useRestaurantMemberships(
  userId: string | undefined,
  isSuperAdmin?: boolean
) {
  const queryKey = isSuperAdmin
    ? userId
      ? queryKeys.restaurants.impersonationList(userId)
      : ["restaurants-impersonation-list", "__none__"]
    : userId
      ? queryKeys.user.restaurantMemberships(userId)
      : ["user-roles", "__none__"];

  const query = useQuery({
    queryKey,
    enabled: Boolean(userId),
    queryFn: async (): Promise<RestaurantMembership[]> => {
      if (!userId) return [];

      if (isSuperAdmin) {
        const [restaurantsRes, urrRows] = await Promise.all([
          supabase
            .from("restaurants")
            .select("id, slug, name, logo_url")
            .order("name", { ascending: true }),
          await supabase
            .from("user_roles")
            .select(
              `
          id,
          role,
          restaurant_id,
          restaurants ( slug, name, logo_url )
        `
            )
            .eq("user_id", userId)
            .eq("is_active", true),
        ]);

        if (restaurantsRes.error) throw restaurantsRes.error;

        return mergeSuperadminMemberships(
          restaurantsRes.data ?? [],
          urrRows.data ?? []
        );
      }

      const { data, error } = await supabase
        .from("user_roles")
        .select(
          `
          id,
          role,
          restaurant_id,
          restaurants ( slug, name, logo_url )
        `
        )
        .eq("user_id", userId)
        .eq("is_active", true);

      if (error) throw error;

      const result = data.map(mapMembershipRow);

      return result;
    },
  });

  return query;
}

/** Inactive `user_roles` count for the signed-in user (used by RestaurantGuard empty state). */
export function useInactiveRestaurantMembershipCount(
  userId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: userId
      ? queryKeys.user.inactiveRestaurantMemberships(userId)
      : ["user-roles-inactive-count", "__none__"],
    enabled: Boolean(userId) && enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;
      const { count, error } = await supabase
        .from("user_roles")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("is_active", false);

      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useRestaurant() {
  const restaurantId = useRestaurantId();

  return useQuery({
    queryKey: queryKeys.restaurants.settings(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("restaurants")
        .select("*")
        .eq("id", restaurantId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}

/**
 * Loads branding/contact fields for a tenant by slug (`queryKeys.restaurants.bySlug`).
 * URL slug from `usePublicTenantSlug()` wins when set; pass `slugOverride` when there is no `:slug` in the path (e.g. staff customer display with store `activeSlug`).
 */
export function useRestaurantBySlug(slugOverride?: string) {
  let slug = usePublicTenantSlug();

  if (slugOverride) {
    slug = slugOverride;
  }

  return useQuery({
    queryKey: queryKeys.restaurants.bySlug(slug),
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("restaurants")
        .select(
          "id, name, logo_url, tagline, background_image_url, background_video_url, category_menu_banner_url, category_menu_banner_height, address_street, postal_code, city, phone_number, organization_number, receipt_footer_text, heading_font, body_font, accent_color, primary_color, background_color, foreground_color, muted_color, border_color, card_color, sidebar_color"
        )
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function usePublicRestaurantSettings(restaurantId: string | undefined) {
  return useQuery({
    queryKey: restaurantId
      ? queryKeys.publicOrder.themeSettings(restaurantId)
      : ["public-order-theme-settings", "__none__"],
    enabled: Boolean(restaurantId),
    queryFn: async () => {
      const id = restaurantId as string;
      const { data, error } = await supabase
        .from("restaurant_settings")
        .select("restaurant_id, order_theme_mode")
        .eq("restaurant_id", id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

/** Public `/order/:slug`: Swedbank toggles via RPC (anon-safe, no bank fields). */
export function usePublicPaymentFlags(restaurantId: string | undefined) {
  return useQuery({
    queryKey: restaurantId
      ? queryKeys.publicOrder.paymentFlags(restaurantId)
      : ["public-order-payment-flags", "__none__"],
    enabled: Boolean(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "public_swedbank_checkout_flags"
      );
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return row as {
        swedbank_pay_enabled: boolean;
        swedbank_pay_online_enabled: boolean;
      } | null;
    },
  });
}

export function useRestaurantSettings() {
  const restaurantId = useRestaurantId();

  return useQuery({
    queryKey: queryKeys.restaurants.settingsRow(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("restaurant_settings")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}

/** `payment_settings` row for the active tenant (admin Settings → payment tab). */
export function usePaymentSettings() {
  const restaurantId = useRestaurantId();

  return useQuery({
    queryKey: queryKeys.restaurants.paymentSettings(restaurantId),
    enabled: Boolean(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_settings")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}
